import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AddressElement, Elements } from '@stripe/react-stripe-js';
import type {
  Appearance,
  StripeElementLocale,
  StripeAddressElementChangeEvent,
} from '@stripe/stripe-js';

import { getStripePromise } from 'utils/stripeClient';

import * as Ch from './styled';
import { isValidEmailAddress } from './invoicePayerEmailsSchema';
import {
  invoiceBillingDefaultsKey,
  MAX_BILLING_NAME_LENGTH,
  type InvoiceBillingDefaults,
} from './invoiceBillingDefaults';

export { MAX_BILLING_NAME_LENGTH };
export const MAX_BILLING_EMAILS = 5;

const BILLING_EMAIL_PLACEHOLDER = 'accounting@club.ch';
/** CHF invoice billing is always Switzerland → single VAT type, no type picker. */
const CHF_TAX_ID_TYPE = 'ch_vat' as const;
const CHF_VAT_PLACEHOLDER = 'CHE-123.456.789 MWST';

export type InvoiceBillingTaxId = {
  businessName: string;
  taxId: string;
  taxIdType: typeof CHF_TAX_ID_TYPE;
  externalTaxIdType: typeof CHF_TAX_ID_TYPE;
};

export type InvoiceBillingData = {
  name: string;
  address: StripeAddressElementChangeEvent['value'];
  taxId: InvoiceBillingTaxId | null;
  billingEmails: string[];
};

export type InvoiceChfBillingHandle = {
  getBillingData: () => InvoiceBillingData | null;
};

type InvoiceChfBillingFieldsProps = {
  active: boolean;
  defaultCountry?: string;
  /** Prefill from Redux / GET subscriptions (name, address, tax, emails). */
  defaults?: InvoiceBillingDefaults;
  onReadyChange?: (ready: boolean) => void;
};

const stripeAppearance: Appearance = {
  theme: 'stripe',
  labels: 'floating',
  variables: {
    borderRadius: '10px',
    fontSizeBase: '14px',
    colorText: '#304659',
    colorTextSecondary: '#4F667A',
    colorTextPlaceholder: '#8298AB',
    colorPrimary: '#33ABA7',
    colorDanger: '#C53030',
  },
  rules: {
    '.Label': {
      transition: 'none',
      fontWeight: '400',
    },
    '.Label--resting': {
      color: '#8298AB',
      fontSize: '14px',
      opacity: '1',
    },
    // Disappear instead of floating to the border (no animated / above label).
    '.Label--floating': {
      opacity: '0',
      fontSize: '0px',
    },
    '.Input': {
      border: '1.5px solid #E5E9E9',
      boxShadow: 'none',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
    '.Input:focus': {
      border: '1.5px solid #33ABA7',
      boxShadow: 'none',
      outline: 'none',
    },
  },
};

type InnerProps = {
  defaultCountry: string;
  defaults: InvoiceBillingDefaults;
  onReadyChange?: (ready: boolean) => void;
};

const InvoiceChfBillingFieldsInner = forwardRef<
  InvoiceChfBillingHandle,
  InnerProps
>(function InvoiceChfBillingFieldsInner(
  { defaultCountry, defaults, onReadyChange },
  ref,
) {
  const { t } = useTranslation();
  const [name, setName] = useState(defaults.name);
  const [addressData, setAddressData] = useState<
    StripeAddressElementChangeEvent['value'] | null
  >(null);
  const [addressComplete, setAddressComplete] = useState(false);
  const [vatNumber, setVatNumber] = useState(defaults.taxId?.taxId ?? '');
  const [emails, setEmails] = useState<string[]>(defaults.billingEmails);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailFocused, setEmailFocused] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const nameEditedRef = useRef(false);

  useEffect(() => {
    if (nameEditedRef.current) return;
    setName(defaults.name);
  }, [defaults.name]);

  useEffect(() => {
    setVatNumber(defaults.taxId?.taxId ?? '');
  }, [defaults.taxId?.taxId]);

  const trimmedName = name.trim().slice(0, MAX_BILLING_NAME_LENGTH);
  const trimmedVat = vatNumber.trim();
  const taxIdData: InvoiceBillingTaxId | null = trimmedVat
    ? {
        businessName: '',
        taxId: trimmedVat,
        taxIdType: CHF_TAX_ID_TYPE,
        externalTaxIdType: CHF_TAX_ID_TYPE,
      }
    : null;
  const ready = Boolean(trimmedName) && addressComplete;

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  useImperativeHandle(
    ref,
    () => ({
      getBillingData: () => {
        if (!addressData || !trimmedName) return null;
        return {
          name: trimmedName,
          address: addressData,
          taxId: taxIdData,
          billingEmails: emails,
        };
      },
    }),
    [addressData, trimmedName, taxIdData, emails],
  );

  const addressOptions = useMemo(
    () => ({
      mode: 'billing' as const,
      allowedCountries: [defaultCountry],
      // Requires `address_element_hide_name_field_beta_1` on loadStripe.
      // Debtor name is our "Organization or name" field above.
      fields: {
        name: 'never' as const,
      },
      defaultValues: {
        address: {
          country: defaults.address?.country || defaultCountry,
          ...(defaults.address?.line1 ? { line1: defaults.address.line1 } : {}),
          ...(defaults.address?.line2 ? { line2: defaults.address.line2 } : {}),
          ...(defaults.address?.city ? { city: defaults.address.city } : {}),
          ...(defaults.address?.state ? { state: defaults.address.state } : {}),
          ...(defaults.address?.postal_code
            ? { postal_code: defaults.address.postal_code }
            : {}),
        },
      },
    }),
    [defaultCountry, defaults.address],
  );

  const commitEmailDraft = useCallback(
    (raw: string) => {
      const parts = raw
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (!parts.length) {
        setEmailDraft('');
        setEmailError(null);
        return;
      }
      let accepted = emails;
      let rejected = '';
      let error: string | null = null;
      for (const part of parts) {
        if (accepted.length >= MAX_BILLING_EMAILS) {
          error = t('subscriptions.checkout.chf.maxRecipients');
          rejected = part;
          break;
        }
        if (!isValidEmailAddress(part)) {
          error = t('subscriptions.checkout.chf.invalidEmail');
          rejected = part;
          break;
        }
        if (!accepted.includes(part)) accepted = [...accepted, part];
      }
      setEmails(accepted);
      setEmailDraft(rejected);
      setEmailError(error);
    },
    [emails, t],
  );

  const removeEmail = useCallback((value: string) => {
    setEmails((prev) => prev.filter((email) => email !== value));
    setEmailError(null);
  }, []);

  const onEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        commitEmailDraft(emailDraft);
        return;
      }
      if (e.key === 'Backspace' && !emailDraft && emails.length) {
        e.preventDefault();
        removeEmail(emails[emails.length - 1]);
      }
    },
    [commitEmailDraft, emailDraft, emails, removeEmail],
  );

  const atRecipientLimit = emails.length >= MAX_BILLING_EMAILS;

  return (
    <>
      <Ch.InvoiceFieldGroup>
        <Ch.InvoiceFieldLabel htmlFor="chf-billing-name">
          {t('subscriptions.checkout.chf.name')}
        </Ch.InvoiceFieldLabel>
        <Ch.InvoiceTextInput
          id="chf-billing-name"
          name="billingName"
          type="text"
          autoComplete="organization"
          maxLength={MAX_BILLING_NAME_LENGTH}
          aria-label={t('subscriptions.checkout.chf.name')}
          value={name}
          onChange={(e) => {
            nameEditedRef.current = true;
            setName(e.target.value);
          }}
        />
      </Ch.InvoiceFieldGroup>

      <Ch.InvoiceFieldGroup>
        <Ch.InvoiceFieldLabel as="span">
          {t('subscriptions.checkout.chf.address')}
        </Ch.InvoiceFieldLabel>
        <Ch.StripeElementField>
          <AddressElement
            options={addressOptions}
            onChange={(event) => {
              setAddressComplete(event.complete);
              setAddressData(event.complete ? event.value : null);
            }}
          />
        </Ch.StripeElementField>
      </Ch.InvoiceFieldGroup>

      <Ch.InvoiceFieldGroup>
        <Ch.InvoiceFieldLabel htmlFor="chf-billing-vat">
          {t('subscriptions.checkout.chf.vatNumber')}
          <Ch.InvoiceOptionalTag>
            {t('subscriptions.checkout.chf.optional')}
          </Ch.InvoiceOptionalTag>
        </Ch.InvoiceFieldLabel>
        <Ch.InvoiceTextInput
          id="chf-billing-vat"
          name="billingVat"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={CHF_VAT_PLACEHOLDER}
          aria-label={t('subscriptions.checkout.chf.vatNumber')}
          value={vatNumber}
          onChange={(e) => setVatNumber(e.target.value)}
        />
      </Ch.InvoiceFieldGroup>

      <Ch.InvoiceFieldGroup>
        <Ch.InvoiceFieldLabel htmlFor="chf-billing-email">
          {t('subscriptions.checkout.chf.billingEmail')}
          <Ch.InvoiceOptionalTag>
            {t('subscriptions.checkout.chf.optional')}
          </Ch.InvoiceOptionalTag>
        </Ch.InvoiceFieldLabel>
        <Ch.InvoiceChipField
          $focused={emailFocused}
          onClick={() => emailInputRef.current?.focus()}
        >
          {emails.map((email) => (
            <Ch.InvoiceChip key={email}>
              <Ch.InvoiceChipText>{email}</Ch.InvoiceChipText>
              <Ch.InvoiceChipRemove
                type="button"
                onClick={() => removeEmail(email)}
                aria-label={t('subscriptions.checkout.invoiceRemoveEmail')}
              >
                ×
              </Ch.InvoiceChipRemove>
            </Ch.InvoiceChip>
          ))}
          <Ch.InvoiceChipInput
            ref={emailInputRef}
            id="chf-billing-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={emailDraft}
            placeholder={atRecipientLimit ? '' : BILLING_EMAIL_PLACEHOLDER}
            aria-invalid={Boolean(emailError)}
            aria-describedby="chf-billing-email-hint"
            onFocus={() => setEmailFocused(true)}
            onBlur={() => {
              setEmailFocused(false);
              commitEmailDraft(emailDraft);
            }}
            onKeyDown={onEmailKeyDown}
            onChange={(e) => {
              const { value } = e.target;
              if (value.includes(',')) {
                commitEmailDraft(value);
                return;
              }
              setEmailDraft(value);
              setEmailError(null);
            }}
          />
        </Ch.InvoiceChipField>
        {emailError ? (
          <Ch.InvoiceFieldError role="alert">{emailError}</Ch.InvoiceFieldError>
        ) : null}
        <Ch.InvoiceHint id="chf-billing-email-hint">
          {t('subscriptions.checkout.chf.recipientsHint')}
        </Ch.InvoiceHint>
      </Ch.InvoiceFieldGroup>
    </>
  );
});

const stripePromise = getStripePromise();

const EMPTY_DEFAULTS: InvoiceBillingDefaults = {
  name: '',
  address: null,
  taxId: null,
  billingEmails: [],
};

const InvoiceChfBillingFields = forwardRef<
  InvoiceChfBillingHandle,
  InvoiceChfBillingFieldsProps
>(function InvoiceChfBillingFields(
  { active, defaultCountry = 'CH', defaults = EMPTY_DEFAULTS, onReadyChange },
  ref,
) {
  const { t, i18n } = useTranslation();
  const remountKey = invoiceBillingDefaultsKey(defaults);

  if (!active) return null;

  if (!stripePromise) {
    return (
      <Ch.InvoiceFormSection>
        <Ch.InvoiceFormError role="alert">
          {t('subscriptions.checkout.invoiceBillingStripeMissing')}
        </Ch.InvoiceFormError>
      </Ch.InvoiceFormSection>
    );
  }

  return (
    <Ch.InvoiceFormSection aria-labelledby="invoice-billing-heading">
      <Ch.InvoiceSectionHeader>
        <Ch.InvoiceSectionIcon aria-hidden>📄</Ch.InvoiceSectionIcon>
        <Ch.InvoiceFormSectionTitle id="invoice-billing-heading">
          {t('subscriptions.checkout.chf.title')}
        </Ch.InvoiceFormSectionTitle>
      </Ch.InvoiceSectionHeader>
      <Ch.InvoiceSectionSubtitle>
        {t('subscriptions.checkout.chf.subtitle')}
      </Ch.InvoiceSectionSubtitle>
      <Elements
        key={remountKey}
        stripe={stripePromise}
        options={{
          appearance: stripeAppearance,
          locale: (i18n.language?.split('-')[0] || 'en') as StripeElementLocale,
        }}
      >
        <InvoiceChfBillingFieldsInner
          ref={ref}
          defaultCountry={defaultCountry}
          defaults={defaults}
          onReadyChange={onReadyChange}
        />
      </Elements>
    </Ch.InvoiceFormSection>
  );
});

export default InvoiceChfBillingFields;
